export type DateOnly = string;

export const MONTH_NAMES = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December",
] as const;
export const WEEKDAY_INITIALS = ["S", "M", "T", "W", "T", "F", "S"] as const;
export const DATE_ONLY_FORMAT = {
	SHORT: "MM/DD/YY",
	LONG: "MM/DD/YYYY",
} as const;

export type DateOnlyFormat =
	(typeof DATE_ONLY_FORMAT)[keyof typeof DATE_ONLY_FORMAT];

export interface DateParts {
	year: number;
	month: number;
	day: number;
}

const pad = (value: number, length = 2) => String(value).padStart(length, "0");

export const getDaysInMonth = (year: number, month: number) =>
	new Date(Date.UTC(year, month, 0)).getUTCDate();

export const createDateOnly = ({ year, month, day }: DateParts): DateOnly =>
	`${pad(year, 4)}-${pad(month)}-${pad(day)}`;

export const parseDateOnly = (value: DateOnly | null): DateParts | null => {
	if (value === null) return null;
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
	if (match === null) return null;

	const year = Number(match[1]);
	const month = Number(match[2]);
	const day = Number(match[3]);

	if (month < 1 || month > 12) return null;
	if (day < 1 || day > getDaysInMonth(year, month)) return null;

	return { year, month, day };
};

export const isValidDateOnly = (value: DateOnly | null): value is DateOnly =>
	parseDateOnly(value) !== null;

export const getToday = (): DateOnly => {
	const now = new Date();
	return createDateOnly({
		year: now.getFullYear(),
		month: now.getMonth() + 1,
		day: now.getDate(),
	});
};

export const isDateBefore = (left: DateOnly, right: DateOnly) => left < right;
export const isDateAfter = (left: DateOnly, right: DateOnly) => left > right;

export const isSameMonth = (left: DateOnly, right: DateOnly) =>
	left.slice(0, 7) === right.slice(0, 7);

export const addDays = (value: DateOnly, days: number): DateOnly => {
	const parts = parseDateOnly(value);
	if (parts === null) return value;
	const shifted = new Date(
		Date.UTC(parts.year, parts.month - 1, parts.day + days),
	);
	return createDateOnly({
		year: shifted.getUTCFullYear(),
		month: shifted.getUTCMonth() + 1,
		day: shifted.getUTCDate(),
	});
};

export const addMonths = (value: DateOnly, months: number): DateOnly => {
	const parts = parseDateOnly(value);
	if (parts === null) return value;

	const monthIndex = parts.month - 1 + months;
	const year = parts.year + Math.floor(monthIndex / 12);
	const month = (((monthIndex % 12) + 12) % 12) + 1;

	return createDateOnly({
		year,
		month,
		day: Math.min(parts.day, getDaysInMonth(year, month)),
	});
};

export const getStartOfMonth = (value: DateOnly): DateOnly =>
	`${value.slice(0, 7)}-01`;

export const getMonthGrid = (value: DateOnly): DateOnly[] => {
	const parts = parseDateOnly(getStartOfMonth(value));
	if (parts === null) return [];

	const firstWeekday = new Date(
		Date.UTC(parts.year, parts.month - 1, 1),
	).getUTCDay();
	const gridStart = addDays(getStartOfMonth(value), -firstWeekday);

	return Array.from({ length: 42 }, (_, index) => addDays(gridStart, index));
};

export const formatDateOnly = (
	value: DateOnly | null,
	format: DateOnlyFormat = DATE_ONLY_FORMAT.LONG,
): string => {
	const parts = parseDateOnly(value);
	if (parts === null) return "";

	const year =
		format === DATE_ONLY_FORMAT.SHORT
			? pad(parts.year % 100)
			: pad(parts.year, 4);
	return `${pad(parts.month)}/${pad(parts.day)}/${year}`;
};

export const parseTypedDate = (
	input: string,
	referenceYear = new Date().getFullYear(),
): DateOnly | null => {
	const trimmed = input.trim();
	if (trimmed === "") return null;

	const segments = trimmed.split(/[/\-.]/).filter((part) => part !== "");
	let month: number;
	let day: number;
	let year: number;

	if (segments.length >= 2 && segments.every((part) => /^\d+$/.test(part))) {
		month = Number(segments[0]);
		day = Number(segments[1]);
		year = segments.length >= 3 ? Number(segments[2]) : referenceYear;
	} else {
		const digits = trimmed.replace(/\D/g, "");
		if (digits.length === 4) {
			month = Number(digits.slice(0, 2));
			day = Number(digits.slice(2, 4));
			year = referenceYear;
		} else if (digits.length === 6 || digits.length === 8) {
			month = Number(digits.slice(0, 2));
			day = Number(digits.slice(2, 4));
			year = Number(digits.slice(4));
		} else {
			return null;
		}
	}

	if (year < 100) year += 2000;

	const candidate = createDateOnly({ year, month, day });
	return isValidDateOnly(candidate) ? candidate : null;
};
